import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import Sidebar from "../side_bar/SidebarAdmin";
import avatar from "../../../assets/avatar.png";
import { useAuth } from "../../../context/AuthContext";
import { updateAdminPassword } from "../../../api/admin/password";
import "./ChangePasswordAdmin.css";
import Header from "../../../components/common/Header";
import { AccountPageProps, accountRoleLabel } from "../../../components/common/accountConfig";

interface ChangePasswordFormAdminProps {
  onSave: (data: { currentPassword: string; newPassword: string }) => void;
  onCancel: () => void;
  saving: boolean;
  profileType?: string;
}

const ChangePasswordFormAdmin: React.FC<ChangePasswordFormAdminProps> = ({
  onSave,
  onCancel,
  saving,
  profileType = "Administrador",
}) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!saving) onSave(formData);
  };

  return (
    <div className="account-panel account-password-panel">
      <div className="account-panel-heading"><h2>Senha de acesso</h2></div>
      <form onSubmit={handleSubmit} aria-label="Alterar senha">
        <fieldset className="account-fields" disabled={saving}>
        <div className="account-field account-field-wide">
          <label htmlFor="password-profile-type" className="form-label">Tipo de perfil</label>
          <input
            id="password-profile-type"
            type="text"
            className="form-control"
            value={profileType}
            readOnly
          />
        </div>

        <div className="account-field">
          <label htmlFor="admin-current-password" className="form-label">Senha atual</label>
          <div className="account-password-input">
            <input
              id="admin-current-password"
              autoComplete="current-password"
              required
              type={showCurrentPassword ? "text" : "password"}
              name="currentPassword"
              className="form-control"
              placeholder="Insira sua senha atual"
              value={formData.currentPassword}
              onChange={handleChange}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              aria-label={showCurrentPassword ? "Ocultar senha atual" : "Mostrar senha atual"}
              aria-pressed={showCurrentPassword}
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            >
              {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="account-field">
          <label htmlFor="admin-new-password" className="form-label">Nova senha</label>
          <div className="account-password-input">
            <input
              id="admin-new-password"
              autoComplete="new-password"
              required
              type={showNewPassword ? "text" : "password"}
              name="newPassword"
              className="form-control"
              placeholder="Nova senha"
              value={formData.newPassword}
              onChange={handleChange}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              aria-label={showNewPassword ? "Ocultar nova senha" : "Mostrar nova senha"}
              aria-pressed={showNewPassword}
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        </fieldset>
        <div className="account-form-actions">
          <button type="button" disabled={saving} onClick={() => {
            setFormData({ currentPassword: "", newPassword: "" });
            setShowCurrentPassword(false);
            setShowNewPassword(false);
            onCancel();
          }} className="btn btn-outline-secondary">
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Salvando…" : "Salvar alterações"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ChangePasswordAdmin: React.FC<AccountPageProps> = ({ role = "Admin" }) => {
  useEffect(() => {
    document.title = "Alterar Senha";
  }, []);
  const { getUserIdFromToken, getCsrfToken, user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formVersion, setFormVersion] = useState(0);

  const clearMessages = () =>
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 5000);

  const handleSave = async (data: {
    currentPassword: string;
    newPassword: string;
  }) => {
    if (saving) return;
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      if (!data.currentPassword || !data.newPassword) {
        setError("Preencha todos os campos.");
        return;
      }
      const userId = getUserIdFromToken();
      if (!userId) {
        setError("Usuário não autenticado.");
        return;
      }
      const token = getCsrfToken();
      if (!token) {
        setError("Token não encontrado.");
        return;
      }
      await updateAdminPassword(
        userId,
        data.currentPassword,
        data.newPassword,
        token
      );
      setSuccess("Senha atualizada com sucesso!");
      setFormVersion((version) => version + 1);
      clearMessages();
    } catch (e: any) {
      setError("Erro ao atualizar senha.");
      clearMessages();
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setError("");
    setSuccess("");
  };

  return (
    <div className="account-profile-page">
      <Header />
      <div className="account-profile-layout">
        <div className="account-navigation">
          <Sidebar
            role={role}
            activeItem="alterar-senha"
            avatar={avatar}
            name={user?.nome_completo || "Administrador"}
          />
        </div>
        <main className="account-profile-content">
          <div className="account-page-heading">
            <h1>Alterar senha</h1>
          </div>

          {error && (
            <div
              className="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError("")}
              ></button>
            </div>
          )}
          {success && (
            <div
              className="alert alert-success alert-dismissible fade show"
              role="alert"
            >
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess("")}
              ></button>
            </div>
          )}
          {saving && (
            <div
              className="alert alert-info d-flex align-items-center"
              role="alert"
            >
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></div>
              Salvando...
            </div>
          )}
          <ChangePasswordFormAdmin
            profileType={accountRoleLabel(role)}
            key={formVersion}
            saving={saving}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </main>
      </div>
    </div>
  );
};

export default ChangePasswordAdmin;
